/*
  Cart cross-sell ("Complete your After Five look"). Owner: K.

  The list is rendered by snippets/af-cart-upsell.liquid, in the cart drawer and on the cart
  page, and comes back with the cart's section on every change. Compact cards: a photograph
  with the name and the price under it, and one small add control on the photograph.
  This script:
    1. adds without leaving the drawer or the page, the same way assets/product-form.js does
       (cart/add with the surface's sections). A product with one variant is added by its
       control at once (a form; without JavaScript it posts to /cart/add). A product with
       sizes or colours opens its choice in place: the tray under the row of cards, filled
       from the card's <template>. Choosing a size adds it; a colour is chosen first when
       the product has several, and the card's photograph follows the colour.
       In the drawer the answer goes to cartDrawer.renderContents(); on the cart page to
       CartItems.renderAdded() (assets/cart.js), which puts the page's sections in place.
    2. moves the list one card at a time with the previous / next buttons (shown from
       990px, where the list cannot be swiped), and marks the one that has nowhere to go.
       The buttons scroll to a card, not by a distance: the card the list is heading for
       is remembered while it moves, so quick clicks add up and the list always comes to
       rest on a card. When every card is in view the pair is not shown.
    3. brings the whole card into view when a control in it takes keyboard focus, and the
       tray into view above the drawer's pinned block when it opens.
  Every listener is delegated from document, because the list is replaced on each change.
  No sentence is typed here: labels come from the markup (data attributes printed by Liquid).
*/
(function () {
  // list -> { index, timer }: the card a scroll started here is heading for. Forgotten
  // once the list has stopped moving, so a swipe or a drag is read from scrollLeft again.
  var heading = new WeakMap();
  // tray -> { card, trigger, variants, selected, busy }: the size choice that is open.
  var choices = new WeakMap();

  var reducedMotion = function () {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  };

  /* ---------------------------------------------------------------------------
     Previous / next
     ------------------------------------------------------------------------ */
  var setArrows = function (list) {
    var section = list.closest('.af-cart-upsell');
    var nav = section && section.querySelector('.af-cart-upsell__nav');
    var prev = section && section.querySelector('[data-af-upsell-prev]');
    var next = section && section.querySelector('[data-af-upsell-next]');
    if (!prev || !next) return;
    var max = list.scrollWidth - list.clientWidth;
    prev.setAttribute('aria-disabled', list.scrollLeft <= 1 ? 'true' : 'false');
    next.setAttribute('aria-disabled', list.scrollLeft >= max - 1 ? 'true' : 'false');
    // Nothing to scroll (every card is in view): no arrows. A list that is not laid out
    // (clientWidth 0) is left as it is.
    if (nav && list.clientWidth > 0) nav.toggleAttribute('data-af-upsell-fits', max <= 1);
  };

  var setAllArrows = function () {
    Array.prototype.forEach.call(document.querySelectorAll('.af-cart-upsell__list'), setArrows);
  };

  // The distance from one card to the next.
  var stepOf = function (list) {
    var card = list.querySelector('.af-cart-upsell__item');
    var gap = parseFloat(getComputedStyle(list).columnGap) || 0;
    return card ? card.getBoundingClientRect().width + gap : list.clientWidth;
  };

  var settleSoon = function (list, state) {
    clearTimeout(state.timer);
    state.timer = setTimeout(function () {
      heading.delete(list);
    }, 200);
  };

  var currentIndex = function (list) {
    var state = heading.get(list);
    return state ? state.index : Math.round(list.scrollLeft / stepOf(list));
  };

  var scrollToIndex = function (list, index, smooth) {
    var step = stepOf(list);
    var max = list.scrollWidth - list.clientWidth;
    var last = max > 1 ? Math.ceil(max / step - 0.01) : 0; // the end of the list is the last stop
    var state = heading.get(list) || {};
    state.index = Math.max(0, Math.min(index, last));
    heading.set(list, state);
    settleSoon(list, state);
    list.scrollTo({ left: state.index * step, behavior: smooth ? 'smooth' : 'auto' });
  };

  /* ---------------------------------------------------------------------------
     The size choice (tray)
     ------------------------------------------------------------------------ */
  var trayOf = function (section) {
    return section ? section.querySelector('[data-af-upsell-tray]') : null;
  };

  var showError = function (section, message) {
    var error = section && section.querySelector('[data-af-upsell-error]');
    if (!error) return;
    error.textContent = message || '';
    error.hidden = !message;
  };

  // Keeps the element clear of the block pinned to the bottom of the drawer (the offer bar
  // with the summary, or the summary alone on a short screen: assets/af-cart.css decides,
  // and cartDrawer.getPinned() finds it); on the cart page the browser's own scrolling does it.
  var reveal = function (element) {
    var scroller = element.closest('.af-cart-drawer__scroll');
    if (!scroller) {
      if (element.scrollIntoView) element.scrollIntoView({ block: 'nearest', behavior: reducedMotion() ? 'auto' : 'smooth' });
      return;
    }
    var drawer = scroller.closest('cart-drawer');
    var pinned = drawer && typeof drawer.getPinned === 'function' ? drawer.getPinned() : null;
    var limit = pinned ? pinned.getBoundingClientRect().top : scroller.getBoundingClientRect().bottom;
    var over = element.getBoundingClientRect().bottom + 8 - limit;
    if (over > 0) scroller.scrollBy({ top: over, behavior: reducedMotion() ? 'auto' : 'smooth' });
  };

  var closeTray = function (section, refocus) {
    var tray = trayOf(section);
    if (!tray || tray.hidden) return;
    var choice = choices.get(tray);
    var hadFocus = tray.contains(document.activeElement);
    tray.hidden = true;
    tray.textContent = '';
    choices.delete(tray);
    if (choice) {
      choice.trigger.setAttribute('aria-expanded', 'false');
      choice.card.classList.remove('is-choosing');
      if ((refocus || hadFocus) && choice.trigger.isConnected) choice.trigger.focus();
    }
  };

  // The variant the current choice names, with `value` in the place of option `index`.
  var variantFor = function (choice, index, value) {
    return choice.variants.find(function (variant) {
      return variant.options.every(function (option, i) {
        return i === index ? option === value : option === choice.selected[i];
      });
    });
  };

  // What can be chosen, for what is chosen so far. The last group adds (a size); the groups
  // before it are a selection (a colour). A value with nothing in stock keeps its label,
  // struck through, and cannot be pressed.
  var paintTray = function (tray) {
    var choice = choices.get(tray);
    if (!choice) return;
    var groups = tray.querySelectorAll('[data-af-upsell-option]');
    var soldOut = tray.closest('.af-cart-upsell').getAttribute('data-sold-out') || '';
    Array.prototype.forEach.call(groups, function (group, position) {
      var index = parseInt(group.getAttribute('data-af-upsell-option'), 10);
      var last = position === groups.length - 1;
      Array.prototype.forEach.call(group.querySelectorAll('[data-value]'), function (button) {
        var value = button.getAttribute('data-value');
        var available;
        if (last) {
          var variant = variantFor(choice, index, value);
          available = !!variant && variant.available;
        } else {
          available = choice.variants.some(function (variant) {
            return variant.available && variant.options[index] === value;
          });
          var selected = choice.selected[index] === value;
          button.classList.toggle('is-selected', selected);
          button.setAttribute('aria-pressed', selected ? 'true' : 'false');
        }
        button.classList.toggle('is-unavailable', !available);
        if (available) {
          button.removeAttribute('aria-disabled');
          button.removeAttribute('aria-label');
        } else {
          button.setAttribute('aria-disabled', 'true');
          if (soldOut) button.setAttribute('aria-label', value + ', ' + soldOut);
        }
      });
    });
  };

  var openTray = function (trigger) {
    var card = trigger.closest('.af-cart-upsell__item');
    var section = trigger.closest('.af-cart-upsell');
    var tray = trayOf(section);
    var template = card && card.querySelector('template[data-af-upsell-options]');
    if (!tray || !template) return;

    closeTray(section, false);
    showError(section, '');
    tray.appendChild(template.content.cloneNode(true));

    var data = tray.querySelector('[data-af-upsell-variants]');
    var variants = [];
    try {
      variants = JSON.parse(data.textContent);
    } catch (error) {
      console.error(error);
    }
    if (!variants.length) {
      tray.textContent = '';
      return;
    }
    var first = variants.find(function (variant) { return variant.available; }) || variants[0];
    choices.set(tray, { card: card, trigger: trigger, variants: variants, selected: first.options.slice(), busy: false });

    tray.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');
    card.classList.add('is-choosing');
    paintTray(tray);

    // Focus goes into the choice (it sits after the whole row in the page, so Tab from the
    // card would pass every other card first). Escape or the close button brings it back.
    var target = tray.querySelector('[data-value]:not([aria-disabled="true"])') || tray.querySelector('[data-af-upsell-close]');
    if (target) target.focus({ preventScroll: true });
    reveal(tray);
  };

  /* ---------------------------------------------------------------------------
     Adding
     ------------------------------------------------------------------------ */
  var unique = function (list) {
    return list.filter(function (value, index) { return value && list.indexOf(value) === index; });
  };

  // origin: the control that was used. Returns false when the add cannot be done here (the
  // caller then lets the browser do what it would without this script).
  var add = function (origin, formData, setBusy) {
    var section = origin.closest('.af-cart-upsell');
    var page = origin.closest('cart-items');
    var drawer = document.querySelector('cart-drawer');
    var onPage = !!page && typeof page.renderAdded === 'function';
    if (typeof fetchConfig !== 'function' || !window.routes) return false;
    if (!onPage && (!drawer || typeof drawer.renderContents !== 'function')) return false;

    showError(section, '');
    setBusy(true);

    var config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    var sections = onPage
      ? page.getSectionsToRender().map(function (entry) { return entry.section; })
      : drawer.getSectionsToRender().map(function (entry) { return entry.id; });
    formData.append('sections', unique(sections));
    formData.append('sections_url', window.location.pathname);
    config.body = formData;

    fetch(window.routes.cart_add_url, config)
      .then(function (response) {
        return response.json();
      })
      .then(function (response) {
        if (response.status) {
          setBusy(false);
          showError(section, response.description || response.message || (window.cartStrings && window.cartStrings.error));
          return;
        }
        if (onPage) {
          // Replaces the page's cart sections, this list included, and tells the others.
          page.renderAdded(response, formData.get('id'));
          return;
        }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'af-cart-upsell',
            productVariantId: formData.get('id'),
            cartData: response,
          });
        }
        // Replaces the drawer's contents, this list included.
        drawer.renderContents(response);
      })
      .catch(function (e) {
        console.error(e);
        setBusy(false);
        showError(section, window.cartStrings && window.cartStrings.error);
      });
    return true;
  };

  var busyControl = function (control) {
    return function (busy) {
      if (!control) return;
      control.classList.toggle('is-loading', busy);
      if (busy) control.setAttribute('aria-disabled', 'true');
      else control.removeAttribute('aria-disabled');
      var spinner = control.querySelector('.loading__spinner');
      if (spinner) spinner.classList.toggle('hidden', !busy);
    };
  };

  /* ---------------------------------------------------------------------------
     Listeners
     ------------------------------------------------------------------------ */
  document.addEventListener('click', function (event) {
    var target = event.target.closest ? event.target : null;
    if (!target) return;

    // Previous / next
    var arrow = target.closest('[data-af-upsell-prev], [data-af-upsell-next]');
    if (arrow) {
      var navSection = arrow.closest('.af-cart-upsell');
      var list = navSection && navSection.querySelector('.af-cart-upsell__list');
      if (!list || arrow.getAttribute('aria-disabled') === 'true') return;
      scrollToIndex(list, currentIndex(list) + (arrow.hasAttribute('data-af-upsell-prev') ? -1 : 1), !reducedMotion());
      return;
    }

    // The add control of a product with sizes: opens its choice, or closes it again.
    var trigger = target.closest('[data-af-upsell-open]');
    if (trigger) {
      if (trigger.getAttribute('aria-disabled') === 'true') return;
      if (trigger.getAttribute('aria-expanded') === 'true') closeTray(trigger.closest('.af-cart-upsell'), true);
      else openTray(trigger);
      return;
    }

    if (target.closest('[data-af-upsell-close]')) {
      closeTray(target.closest('.af-cart-upsell'), true);
      return;
    }

    // A value in the open choice.
    var button = target.closest('.af-cart-upsell__tray [data-value]');
    if (!button) return;
    var tray = button.closest('[data-af-upsell-tray]');
    var choice = choices.get(tray);
    if (!choice || choice.busy || button.getAttribute('aria-disabled') === 'true') return;

    var group = button.closest('[data-af-upsell-option]');
    var index = parseInt(group.getAttribute('data-af-upsell-option'), 10);
    var value = button.getAttribute('data-value');
    var groups = tray.querySelectorAll('[data-af-upsell-option]');

    if (group !== groups[groups.length - 1]) {
      // A colour: select it, show its photograph on the card, re-read what is in stock.
      choice.selected[index] = value;
      var shown = choice.variants.find(function (variant) { return variant.options[index] === value && variant.image; });
      var image = choice.card.querySelector('.af-cart-upsell__image');
      if (shown && image) image.src = shown.image;
      paintTray(tray);
      return;
    }

    // A size: add that variant.
    var variant = variantFor(choice, index, value);
    if (!variant || !variant.available) return;
    var formData = new FormData();
    formData.append('id', variant.id);
    formData.append('quantity', 1);
    var triggerBusy = busyControl(choice.trigger);
    var done = add(button, formData, function (busy) {
      choice.busy = busy;
      tray.setAttribute('aria-busy', busy ? 'true' : 'false');
      button.classList.toggle('is-selected', busy);
      triggerBusy(busy);
    });
    if (!done) {
      // No cart script on this page: the product page can add it.
      var link = choice.card.querySelector('.af-cart-upsell__name');
      if (link) window.location.href = link.href;
    }
  });

  // Escape closes an open choice first; the drawer's own Escape (assets/cart-drawer.js, on
  // document in the bubbling phase) then never sees this key press.
  document.addEventListener(
    'keydown',
    function (event) {
      if (event.key !== 'Escape' && event.key !== 'Esc') return;
      var active = document.activeElement;
      var section = active && active.closest ? active.closest('.af-cart-upsell') : null;
      var tray = trayOf(section);
      if (!tray || tray.hidden) return;
      event.preventDefault();
      event.stopPropagation();
      closeTray(section, true);
    },
    true
  );

  // Keyboard focus only: a card under a pointer must not move between press and release.
  document.addEventListener('focusin', function (event) {
    var control = event.target;
    var card = control && control.closest ? control.closest('.af-cart-upsell__item') : null;
    var list = card && card.closest('.af-cart-upsell__list');
    if (!list) return;
    try {
      if (!control.matches(':focus-visible')) return;
    } catch (error) {
      // A browser without :focus-visible: treat every focus as keyboard focus.
    }

    var frame = list.getBoundingClientRect();
    var box = card.getBoundingClientRect();
    if (box.left >= frame.left - 1 && box.right <= frame.right + 1) return;
    scrollToIndex(list, Array.prototype.indexOf.call(list.children, card), false);
  });

  // scroll does not bubble; the capture phase sees it for whichever list is in the page.
  document.addEventListener(
    'scroll',
    function (event) {
      var list = event.target;
      if (!list || !list.matches || !list.matches('.af-cart-upsell__list')) return;
      setArrows(list);
      var state = heading.get(list);
      if (state) settleSoon(list, state);
    },
    true
  );

  // A product with one variant: its control is a form.
  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || !form.matches || !form.matches('form[data-af-upsell-form]')) return;

    var button = form.querySelector('[type="submit"]');
    if (button && button.getAttribute('aria-disabled') === 'true') {
      event.preventDefault();
      return;
    }
    closeTray(form.closest('.af-cart-upsell'), false);
    if (add(form, new FormData(form), busyControl(button))) event.preventDefault();
  });

  // The arrows' state for the lists as they are: at load, when the window changes size, and
  // after every cart change (the lists are replaced then).
  var timer = 0;
  var arrowsSoon = function () {
    clearTimeout(timer);
    timer = setTimeout(setAllArrows, 50);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', arrowsSoon);
  else arrowsSoon();
  window.addEventListener('resize', arrowsSoon);
  window.addEventListener('load', function () {
    arrowsSoon();
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
        setTimeout(setAllArrows, 50);
      });
    }
  });
})();
